export const SIGNATURE_LIBRARY_STORAGE_KEY = 'local-pdf-signer:saved-signatures';
export const SIGNER_PROFILE_STORAGE_KEY = 'local-pdf-signer:profile';
export const SNAPSHOTS_STORAGE_KEY = 'local-pdf-signer:snapshots';
export const UI_PREFS_STORAGE_KEY = 'local-pdf-signer:ui-prefs';

// FX-C6: align per-image budget to server limit (server enforces 2 MB decoded per image,
// 8 MB total). Client now matches server so oversized images are rejected before upload.
export const MAX_SIGNATURE_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB decoded — matches server
export const MAX_SIGNATURE_DATA_URL_LENGTH = Math.ceil(MAX_SIGNATURE_IMAGE_BYTES * 1.37) + 64; // base64 headroom

export interface SignatureFontOption {
  id: string;
  label: string;
  /** CSS font-family name; loaded via the Google Fonts <link> in index.html. */
  family: string;
}

export const SIGNATURE_FONTS: SignatureFontOption[] = [
  { id: 'great-vibes', label: 'Great Vibes', family: 'Great Vibes' },
  { id: 'dancing-script', label: 'Dancing Script', family: 'Dancing Script' },
  { id: 'caveat', label: 'Caveat', family: 'Caveat' },
  { id: 'sacramento', label: 'Sacramento', family: 'Sacramento' },
];

/** Font-family select options for the text format toolbar (Contract #4).
 *  Keys match SignatureFontFamilyKey from utils/fontMapping.ts. */
export const SIGNATURE_FONT_FAMILY_OPTIONS: { key: string; label: string }[] = [
  { key: 'signature-greatvibes', label: 'Great Vibes' },
  { key: 'signature-dancing', label: 'Dancing Script' },
  { key: 'signature-caveat', label: 'Caveat' },
  { key: 'signature-sacramento', label: 'Sacramento' },
];

export interface SignatureInkOption {
  id: string;
  label: string;
  value: string;
}

export const SIGNATURE_INK_COLORS: SignatureInkOption[] = [
  { id: 'black', label: 'Black ink', value: '#0f172a' },
  { id: 'blue', label: 'Blue ink', value: '#2563eb' },
  { id: 'dark-red', label: 'Dark red ink', value: '#9f1239' },
];

// === Annotation tool identifiers (Pass 2) ===
export type AnnotationTool = 'highlight' | 'underline' | 'strikeout' | 'rect' | 'ellipse' | 'cross' | 'text-bubble' | 'ink';

export const ANNOTATION_TOOLS: ReadonlyArray<AnnotationTool> = [
  'highlight',
  'underline',
  'strikeout',
  'rect',
  'ellipse',
  'cross',
  'text-bubble',
  'ink',
];

/** Default visual properties for each annotation tool when placed. */
export const ANNOTATION_TOOL_DEFAULTS: Record<
  AnnotationTool,
  { color?: string; strokeColor?: string; fillColor?: string; lineWidth?: number; opacity?: number; width: number; height: number }
> = {
  highlight: { color: '#ffd54a', opacity: 0.35, width: 120, height: 18 },
  underline: { strokeColor: '#2563eb', lineWidth: 2, width: 120, height: 18 },
  strikeout: { strokeColor: '#e11d48', lineWidth: 2, width: 120, height: 18 },
  rect:      { strokeColor: '#e11d48', lineWidth: 2, width: 120, height: 80 },
  ellipse:   { strokeColor: '#e11d48', lineWidth: 2, width: 120, height: 80 },
  cross:     { strokeColor: '#dc2626', lineWidth: 3, width: 64, height: 64 },
  'text-bubble': { strokeColor: '#2563eb', fillColor: '#eff6ff', lineWidth: 2, opacity: 0.92, width: 180, height: 72 },
  // ink geometry is determined by the drawn stroke; these are fallbacks only
  ink:       { strokeColor: '#0f172a', lineWidth: 2, width: 60, height: 60 },
};

export type WorkflowStage =
  | 'empty'
  | 'uploading'
  | 'ready'
  | 'creating-signature'
  | 'placing'
  | 'dirty'
  | 'applying'
  | 'signed'
  | 'stale'
  | 'error';

export const WORKFLOW_STAGE_LABELS: Record<WorkflowStage, string> = {
  empty: 'Upload PDF',
  uploading: 'Loading PDF',
  ready: 'Ready',
  'creating-signature': 'Create Signature',
  placing: 'Place Signature',
  dirty: 'Review Fields',
  applying: 'Applying',
  signed: 'Signed',
  stale: 'Reapply Needed',
  error: 'Needs Attention',
};
